import React, { useState } from 'react'
import { Drawer } from 'antd'

import { stringifyDate, usePet } from '../../modules'

import Confirm from '../reusable/confirm'
import Receive from '../reusable/receive'
import RecordInsurerPayment from './recordInsurerPayment'

const statusColors = {
    submitted: 'bg-yellow-100 text-yellow-700',
    partially_paid: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700',
    disputed: 'bg-red-100 text-red-700',
    written_off: 'bg-slate-100 text-slate-700',
}

const InsurerSubmissionDetail = ({ visible, id, onClose, onPerform }) => {
    const [payVisible, setPayVisible] = useState(false)

    const [{ loading, error, data: submission }, fetchSubmission] = usePet('insurerSubmission', { id }, { notify: true })

    const close = () => onClose()

    const onDisputeComplete = (_, notif, ops) => {
        notif('Submission has been flagged as disputed', ops('success'))
        fetchSubmission()
        onPerform()
    }

    const [, flagDisputed] = usePet('flagInsurerSubmissionDisputed', { id }, { notify: true, callback: onDisputeComplete, manual: true })

    const onWriteOffComplete = (_, notif, ops) => {
        notif('Submission has been written off', ops('success'))
        fetchSubmission()
        onPerform()
    }

    const [, writeOff] = usePet('writeOffInsurerSubmission', { id }, { notify: true, callback: onWriteOffComplete, manual: true })

    const dispute = () => {
        Confirm({
            title: 'Flag Submission as Disputed?',
            message: 'Please confirm before proceeding with this action',
            okText: 'Flag Disputed',
            onOk: flagDisputed
        })
    }

    const doWriteOff = () => {
        Confirm({
            title: 'Write Off Submission?',
            message: 'This marks the remaining outstanding balance as written off. It does NOT adjust the insurer\'s ledger balance — that still reflects the full outstanding amount until reconciled separately. Please confirm before proceeding.',
            okText: 'Write Off',
            onOk: writeOff
        })
    }

    const outstanding = (submission?.totalAmount ?? 0) - (submission?.paidAmount ?? 0)

    return (
        <Drawer
            closable
            maskClosable
            visible={ visible }
            onClose={ close }
            width="40%"
            title={ submission?.patientData?.name ?? 'Submission Details' }>
            {
                payVisible &&
                <RecordInsurerPayment
                    visible={ payVisible }
                    submissions={ submission ? [submission] : [] }
                    insurer={ submission?.insurerData }
                    onClose={ () => setPayVisible(false) }
                    onPerform={ () => { fetchSubmission(); onPerform() } } />
            }
            <Receive progress loading={ loading } error={ error }>
            {
                submission &&
                <>
                    <div className="mb-4 rounded-sm border border-gray-200 bg-gray-50 px-4 py-3">
                        <div className="grid gap-3 md:grid-cols-2 sm:grid-cols-1">
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Insurer</p>
                                <p className="text-sm font-medium mb-0">{submission?.insurerData?.name ?? '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Scheme</p>
                                <p className="text-sm font-medium mb-0">{submission?.schemeData?.name ?? '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Order No.</p>
                                <p className="text-sm font-medium mb-0">{submission?.orderData?.orderNo ?? '—'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Status</p>
                                <span className={ `inline-flex font-medium rounded-full px-2.5 py-0.5 text-xs ${statusColors[submission?.status] ?? 'bg-slate-100 text-slate-700'}` }>
                                    {submission?.status}
                                </span>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Total Amount</p>
                                <p className="text-sm font-medium mb-0">KSh {Number(submission?.totalAmount ?? 0).toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Paid</p>
                                <p className="text-sm font-medium mb-0">KSh {Number(submission?.paidAmount ?? 0).toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Outstanding</p>
                                <p className="text-sm font-medium mb-0">KSh {Number(outstanding).toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 mb-1">Submitted On</p>
                                <p className="text-sm font-medium mb-0">{stringifyDate(submission?.submittedAt)}</p>
                            </div>
                        </div>
                        {
                            submission?.reference &&
                            <div className="mt-3">
                                <p className="text-xs text-gray-500 mb-1">Reference</p>
                                <p className="text-sm font-medium mb-0">{submission.reference}</p>
                            </div>
                        }
                        {
                            submission?.disputeReason &&
                            <div className="mt-3">
                                <p className="text-xs text-gray-500 mb-1">Dispute Reason</p>
                                <p className="text-sm font-medium mb-0">{submission.disputeReason}</p>
                            </div>
                        }
                    </div>

                    {
                        submission?.status !== 'paid' && submission?.status !== 'written_off' &&
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={ () => setPayVisible(true) }
                                className="text-white hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                                Record Payment
                            </button>
                            <button
                                onClick={ dispute }
                                className="text-white bg-yellow-500 hover:bg-yellow-600 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                                Dispute
                            </button>
                            <button
                                onClick={ doWriteOff }
                                className="text-white bg-gray-500 hover:bg-gray-600 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                                Write Off
                            </button>
                        </div>
                    }

                    {
                        submission?.lifecycle?.length > 0 &&
                        <div className="mt-5">
                            <p className="text-sm font-semibold text-gray-800 mb-2">History</p>
                            <div className="space-y-2">
                                {
                                    submission.lifecycle.slice().reverse().map((event, key) =>
                                        <div key={ key } className="text-xs border-l-2 border-gray-200 pl-3">
                                            <p className="mb-0 font-medium">{event.status}</p>
                                            <p className="mb-0 text-gray-500">{stringifyDate(event.at)}{event.message ? ` — ${event.message}` : ''}</p>
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    }
                </>
            }
            </Receive>
        </Drawer>
    )
}

export default InsurerSubmissionDetail
